import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';

const SETTINGS_DOC_ID = 'global_settings';

/**
 * Gets the global settings document reference
 */
const getSettingsDocRef = () => doc(db, 'settings', SETTINGS_DOC_ID);

/**
 * Gets current global settings
 * @returns {Promise<Object>} Settings object
 */
export const getGlobalSettings = async () => {
  try {
    const settingsDoc = await getDoc(getSettingsDocRef());
    if (settingsDoc.exists()) {
      return settingsDoc.data();
    } else {
      // Return default settings if document doesn't exist
      const defaultSettings = {
        showCacheRefreshButtons: true,
        showConsoleLogs: true,
        updatedAt: new Date(),
        updatedBy: 'system'
      };
      // Create the document with defaults
      await setDoc(getSettingsDocRef(), defaultSettings);
      return defaultSettings;
    }
  } catch (error) {
    console.error('Error getting global settings:', error);
    // Return defaults on error
    return {
      showCacheRefreshButtons: true,
      updatedAt: new Date(),
      updatedBy: 'system'
    };
  }
};

/**
 * Updates global settings
 * @param {Object} updates - Settings to update
 * @param {string} updatedBy - User who made the update
 * @returns {Promise<void>}
 */
export const updateGlobalSettings = async (updates, updatedBy = 'admin') => {
  try {
    const settingsRef = getSettingsDocRef();
    await setDoc(settingsRef, {
      ...updates,
      updatedAt: new Date(),
      updatedBy
    }, { merge: true });
  } catch (error) {
    console.error('Error updating global settings:', error);
    throw error;
  }
};

/**
 * Subscribes to global settings changes
 * @param {function} callback - Callback function to handle settings updates
 * @returns {function} Unsubscribe function
 */
export const subscribeToGlobalSettings = (callback) => {
  try {
    return onSnapshot(getSettingsDocRef(), (doc) => {
      if (doc.exists()) {
        callback(doc.data());
      } else {
        // Call with defaults if document doesn't exist
        callback({
          showCacheRefreshButtons: true,
          showConsoleLogs: true,
          updatedAt: new Date(),
          updatedBy: 'system'
        });
      }
    }, (error) => {
      console.error('Error listening to global settings:', error);
      // Call with defaults on error
      callback({
        showCacheRefreshButtons: true,
        showConsoleLogs: true,
        updatedAt: new Date(),
        updatedBy: 'system'
      });
    });
  } catch (error) {
    console.error('Error setting up settings subscription:', error);
    // Return a no-op unsubscribe function
    return () => {};
  }
};

/**
 * Updates just the cache refresh buttons setting
 * @param {boolean} showButtons - Whether to show cache refresh buttons
 * @param {string} updatedBy - User who made the update
 * @returns {Promise<void>}
 */
export const updateCacheRefreshButtonsSetting = async (showButtons, updatedBy = 'admin') => {
  return updateGlobalSettings({ showCacheRefreshButtons: showButtons }, updatedBy);
};

/**
 * Updates just the console logs setting
 * @param {boolean} showLogs - Whether to show console logs
 * @param {string} updatedBy - User who made the update
 * @returns {Promise<void>}
 */
export const updateConsoleLogsSetting = async (showLogs, updatedBy = 'admin') => {
  return updateGlobalSettings({ showConsoleLogs: showLogs }, updatedBy);
};